import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { Button } from '../../components/base/Button';
import { updateSEO, seoData } from '../../utils/seo';

const AboutPage: React.FC = () => {
  useEffect(() => {
    updateSEO(seoData.about);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(8, 145, 178, 0.8)), url('https://public.readdy.ai/ai/img_res/de97908518221c5efce45f5123284451.jpg')`
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            About <strong>Henatos Limited</strong>
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Building Africa's most reliable trade network for premium hides and skins
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Our <strong>Story</strong>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Henatos Limited began as a local wholesale trader before 2020, with a vision to create 
                transparency and reliability in Africa's hides and skins trade. What started as a small 
                operation has evolved into a pan-African enterprise that connects rural suppliers across 
                East and Southern Africa with ready buyers in Nigeria.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Our journey from local trader to international commodities company reflects our commitment 
                to building sustainable trade relationships that benefit all stakeholders. We've established 
                a structured sourcing network that ensures consistency, quality, and fair value for both 
                suppliers and buyers.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Today, Henatos Limited stands as a testament to what's possible when African businesses 
                prioritize integrity, reliability, and partnership in cross-border trade.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=African%20business%20growth%20timeline%20visualization%2C%20professional%20infographic%20showing%20company%20evolution%20from%20local%20to%20pan-African%20enterprise%2C%20modern%20corporate%20design%20with%20blue%20and%20cyan%20colors%2C%20success%20story%20illustration&width=600&height=400&seq=story1&orientation=landscape"
                alt="Henatos company growth story"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Our <strong>Foundation</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on strong principles that guide every aspect of our business operations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Mission */}
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-target-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4 text-center">Mission</h3>
              <p className="text-gray-700 text-center text-lg">
                To build a transparent African trade network that delivers quality and reliability, 
                connecting suppliers and buyers across the continent with integrity and trust.
              </p>
            </div>
            
            {/* Vision */}
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-eye-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4 text-center">Vision</h3>
              <p className="text-gray-700 text-center text-lg">
                To be Africa's most trusted name in hides and skins, setting the standard for 
                excellence in cross-border commodity trading across the continent.
              </p>
            </div>
            
            {/* Values */}
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4 text-center">Values</h3>
              <div className="space-y-2 text-center">
                <p className="text-gray-700 font-medium">Integrity</p>
                <p className="text-gray-700 font-medium">Reliability</p>
                <p className="text-gray-700 font-medium">Partnership</p>
                <p className="text-gray-700 font-medium">Sustainability</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact & Sustainability */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=African%20rural%20suppliers%20and%20farmers%20working%20with%20dried%20cow%20hides%2C%20sustainable%20agriculture%20and%20trade%20practices%2C%20community%20empowerment%20through%20fair%20trade%2C%20rural%20development%20in%20East%20Africa%2C%20positive%20social%20impact&width=600&height=400&seq=impact1&orientation=landscape"
                alt="Henatos impact on African communities"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Impact & <strong>Sustainability</strong>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                At Henatos Limited, we believe that successful business goes hand-in-hand with 
                positive social impact. Our operations create meaningful opportunities for rural 
                suppliers across East and Southern Africa while promoting sustainable trade practices.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Empowering Rural Suppliers</h4>
                    <p className="text-gray-600">Providing advance deposits, steady offtake, and technical support to rural communities</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Promoting Ethical Trade</h4>
                    <p className="text-gray-600">Ensuring fair pricing and transparent business practices across our network</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Creating Jobs</h4>
                    <p className="text-gray-600">Supporting employment opportunities in processing, logistics, and quality control</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Reducing Waste</h4>
                    <p className="text-gray-600">Structured supply chains that maximize utilization and minimize waste</p>
                  </div>
                </div>
              </div>
              
              <Button href="/suppliers" variant="primary">
                Join Our Network
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Leadership <strong>Team</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced leadership team brings decades of expertise in finance, 
              operations, and cross-border trade across Africa.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 text-center shadow-lg">
              <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/6b84a11e410079b56c6291a27cbf72c2/238e86974b3472993b8201adea4ff2d3.jpeg"
                  alt="Henry Adebisi"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">Henry Adebisi</h3>
              <p className="text-cyan-600 font-medium mb-4">Co-Founder & Chief Financial Officer</p>
              <p className="text-gray-600">
                Finance leader with over a decade of experience across HRTech, Fintech, 
                and commodity trading. Oversees Henatos' financial, partnerships & commercial strategy. 
                He also founded AfriStakes, supporting emerging ventures with funding opportunities.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 text-center shadow-lg">
              <div className="w-24 h-24 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/6b84a11e410079b56c6291a27cbf72c2/e3aaf860dba3d8be44012277ddd4776c.png"
                  alt="Tosin Adekoya"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">Tosin Adekoya</h3>
              <p className="text-cyan-600 font-medium mb-4">Co-Founder & Chief Operations Officer</p>
              <p className="text-gray-600">
                Trade and logistics professional with extensive experience in cross-border 
                sourcing and market distribution, leading Henatos' operational and commercial 
                strategy across Africa.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 text-center shadow-lg">
              <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/6b84a11e410079b56c6291a27cbf72c2/3d2ab9f4e00f56cef890e9adea8fc4a5.jpeg"
                  alt="Moses Manyara"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">Moses Manyara</h3>
              <p className="text-cyan-600 font-medium mb-4">Head of Operations, East Africa</p>
              <p className="text-gray-600">
                Operations and supply chain leader overseeing sourcing and vendor coordination 
                across Tanzania, Burundi, and Zambia, ensuring adherence to Henatos' strict 
                quality standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ready to Experience the <strong>Henatos Difference?</strong>
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join Africa's most trusted hides and skins network. Whether you're a supplier or buyer, 
            we're here to build lasting partnerships that drive mutual success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/suppliers" variant="secondary" size="lg">
              Become a Supplier
            </Button>
            <Button href="/buyers" variant="outline" size="lg">
              Buyer Inquiry
            </Button>
            <Button href="/contact" variant="primary" size="lg">
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
